import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import WhatsAppFloat from '../components/WhatsAppFloat';
import SEO from '../components/SEO';
import axios from 'axios';
import { PersonSchema, WebsiteSchema } from '../utils/schemas';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function HomePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(API + '/projects');
      setProjects(response.data.projects.slice(0, 4));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="EMABRAW — Edgardo Andrade | Fotografía Documental"
        description="Documento la vida cotidiana de mi país, su espontaneidad y ritmo. Fotografía documental, street photography y proyectos artísticos desde Panamá."
        image="/og-image.jpg"
        type="website"
        keywords="fotografía documental Panamá, Edgardo Andrade, EMABRAW, street photography Panama, fotografía callejera, fotógrafo panameño, arte fotográfico, fotografía artística"
        schema={{
          "@context": "https://schema.org",
          "@graph": [PersonSchema, WebsiteSchema]
        }}
      />

      <Navigation />
      <WhatsAppFloat />

      {/* Hero Section */}
      <section className="min-h-[85vh] md:min-h-screen flex items-center pt-20 md:pt-24 pb-12 md:pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-4 order-2 lg:order-1"
            >
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none tracking-tight">
                EMA<br />BRAW
              </h1>
              <p className="mt-6 text-lg md:text-xl text-neutral-300 italic leading-relaxed max-w-sm">
                "Documento la vida cotidiana de mi país, su espontaneidad y ritmo."
              </p>
              <p className="mt-4 text-sm text-neutral-500 tracking-wide uppercase">
                Edgardo Andrade — Panamá
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-8 order-1 lg:order-2"
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                <img
                  src="https://images.pexels.com/photos/19501488/pexels-photo-19501488.jpeg"
                  alt="Fotografía documental"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Spreads */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 space-y-20 md:space-y-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative overflow-hidden w-full" style={{ aspectRatio: '21/9' }}>
              <img
                src="https://images.unsplash.com/photo-1707485318554-5c0ce475f68c"
                alt="Calles de Panamá"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
          >
            <div className="lg:col-span-7">
              <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <img
                  src="https://images.pexels.com/photos/13796991/pexels-photo-13796991.jpeg"
                  alt="Momento documental"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <blockquote className="text-2xl md:text-3xl text-neutral-300 italic leading-relaxed">
                "La obra de Edgardo nos ofrece, de manera muy íntima, esa experiencia de inmersión dentro del barrio."
              </blockquote>
              <cite className="mt-4 block text-sm text-neutral-500 not-italic">
                — Ileana Forero, curadora de "Pateando Calle"
              </cite>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Explorar */}
      <section className="py-16 md:py-24 border-t border-neutral-800/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white text-center mb-10 md:mb-16"
          >
            Explorar
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => navigate('/proyectos')}
                className="group w-full text-left"
              >
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src="https://images.pexels.com/photos/19501488/pexels-photo-19501488.jpeg"
                    alt="Proyectos"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-500"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white">Proyectos</h3>
                    <p className="mt-1 text-sm text-neutral-300">Fotografía documental, series artísticas y trabajo autoral</p>
                    <p className="mt-2 text-xs text-neutral-500">@emabraw</p>
                  </div>
                </div>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <a
                href="https://cuanto.app/emabraw.pa"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full block"
              >
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src="https://images.unsplash.com/photo-1522897048979-e407743f3603"
                    alt="Tienda"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-500"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white">Tienda</h3>
                    <p className="mt-1 text-sm text-neutral-300">Impresiones y productos</p>
                    <p className="mt-2 text-xs text-neutral-500">cuanto.app/emabraw.pa ↗</p>
                  </div>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-neutral-800/30">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-white">
              Colaboraciones, galerías, prensa
            </h2>
            <p className="mt-4 text-neutral-400">
              Disponible para exposiciones, asignaciones editoriales y trabajo comisionado.
            </p>
            <button
              onClick={() => navigate('/contacto')}
              className="mt-8 inline-block border border-white/20 px-8 py-3 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black transition-all duration-300"
            >
              Contactar
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
